import type { SpeechProvider, SpeechProviderCallbacks, SpeechSession, SpeechStopOptions, SpeechError } from './types';
import { registerActiveSpeechSession, clearActiveSpeechSession, stopActiveSpeechSession } from './speechSessionRegistry';
import { STT_CONFIG } from '@/config/appConfig';

/**
 * Browser-native Web Speech API provider. No account, no key, no cost — proves the
 * `SpeechProvider` abstraction actually swaps (not just a one-implementation
 * interface), and doubles as a way to exercise the mic UI without an AssemblyAI
 * account. Real capability gaps vs. AssemblyAI, by design, not oversight:
 *  - The browser captures audio internally; there's no raw-PCM pipeline to plug
 *    into, no silence-threshold tuning, no control over audio transport (Chrome
 *    sends the audio to Google's speech service, not AssemblyAI — a different,
 *    less documented third party; worth disclosing if this ever ships, since it
 *    touches the annex's "audio not stored on our server" claim).
 *  - No automatic multilingual code-switching like AssemblyAI's
 *    `universal-streaming-multilingual` — language is fixed per session.
 *  - Little to no Firefox support; depends on live network connectivity even in
 *    Chrome, since recognition happens server-side there.
 */

// TypeScript's lib.dom.d.ts has SpeechRecognitionResult/ResultList/Alternative
// (they're shared with speech-synthesis-adjacent APIs) but not SpeechRecognition
// itself, nor its event types, nor `webkitSpeechRecognition` — the Web Speech API
// is still non-standard. Minimal ambient declarations for what this file uses.
declare global {
  interface SpeechRecognition extends EventTarget {
    continuous: boolean;
    interimResults: boolean;
    lang: string;
    onstart: ((this: SpeechRecognition, ev: Event) => unknown) | null;
    onresult: ((this: SpeechRecognition, ev: SpeechRecognitionEvent) => unknown) | null;
    onerror: ((this: SpeechRecognition, ev: SpeechRecognitionErrorEvent) => unknown) | null;
    onend: ((this: SpeechRecognition, ev: Event) => unknown) | null;
    abort(): void;
    start(): void;
    stop(): void;
  }

  // Ambient global constructor declaration — matches how lib.dom.d.ts itself
  // declares SpeechRecognitionResult/etc., `var` is required here, not a style choice.
  // eslint-disable-next-line no-var
  var SpeechRecognition: {
    prototype: SpeechRecognition;
    new (): SpeechRecognition;
  };

  interface SpeechRecognitionEvent extends Event {
    readonly resultIndex: number;
    readonly results: SpeechRecognitionResultList;
  }

  interface SpeechRecognitionErrorEvent extends Event {
    readonly error: string;
    readonly message: string;
  }

  interface Window {
    SpeechRecognition?: typeof SpeechRecognition;
    webkitSpeechRecognition?: typeof SpeechRecognition;
  }
}

const NETWORK_MESSAGE = 'Voice input is unavailable right now. Please type your question.';
const SILENCE_MESSAGE = "We didn't catch that. Try again.";
const FINALIZE_TIMEOUT_MS = STT_CONFIG.finalizeTimeoutMs;

function getRecognitionCtor(): typeof SpeechRecognition | undefined {
  if (typeof window === 'undefined') return undefined;
  return window.SpeechRecognition ?? window.webkitSpeechRecognition;
}

class WebSpeechSession implements SpeechSession {
  private readonly callbacks: SpeechProviderCallbacks;
  private readonly registryEntry: { providerId: 'webspeech'; stop: () => Promise<void> };
  private recognition: SpeechRecognition | null = null;
  private finalizeTimer: ReturnType<typeof setTimeout> | null = null;
  private lastCommittedText: string | null = null;
  private torn = false;
  private stopping = false;
  private selfInitiatedStop = false;
  private silentStop = false;
  private resolveStop: (() => void) | null = null;

  constructor(callbacks: SpeechProviderCallbacks) {
    this.callbacks = callbacks;
    this.registryEntry = { providerId: 'webspeech', stop: () => this.teardown() };
  }

  async start(): Promise<void> {
    await stopActiveSpeechSession();
    this.torn = false;
    this.stopping = false;

    const RecognitionCtor = getRecognitionCtor();
    if (!RecognitionCtor) {
      this.fail({ code: 'unsupported', message: NETWORK_MESSAGE });
      return;
    }

    this.callbacks.onStateChange('starting');

    const recognition = new RecognitionCtor();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = navigator.language || 'en-US';

    recognition.onstart = () => {
      if (this.torn || this.stopping) return;
      this.callbacks.onStateChange('listening');
    };

    recognition.onresult = (event: SpeechRecognitionEvent) => {
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        const text = result[0]?.transcript?.trim();
        if (!text) continue;
        if (result.isFinal) {
          // Web Speech never revises a past final — no replacesPreviousText.
          this.callbacks.onFinal({ text });
          this.lastCommittedText = text;
        } else {
          this.callbacks.onPartial(text);
        }
      }
    };

    recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
      if (this.selfInitiatedStop && event.error === 'aborted') return;
      this.fail(mapRecognitionError(event.error));
    };

    recognition.onend = () => {
      if (this.finalizeTimer) {
        clearTimeout(this.finalizeTimer);
        this.finalizeTimer = null;
      }
      void this.completeStop();
    };

    this.recognition = recognition;
    registerActiveSpeechSession(this.registryEntry);

    try {
      recognition.start();
    } catch (err) {
      await this.teardown();
      this.fail({ code: 'network-or-service', message: NETWORK_MESSAGE, cause: err });
    }
  }

  stop(options?: SpeechStopOptions): Promise<void> {
    if (this.torn) return Promise.resolve();
    if (this.stopping) return new Promise((resolve) => { this.resolveStop = resolve; });
    this.stopping = true;
    this.selfInitiatedStop = true;
    this.silentStop = options?.silent ?? false;
    this.callbacks.onStateChange('finalizing');
    return new Promise((resolve) => {
      this.resolveStop = resolve;
      // .stop() (not .abort()) flushes the in-progress result as a final one.
      // onend isn't always reliable across browsers, so a fallback timeout still
      // tears down rather than leaving "finalizing" stuck forever.
      this.recognition?.stop();
      this.finalizeTimer = setTimeout(() => void this.completeStop(), FINALIZE_TIMEOUT_MS);
    });
  }

  private async completeStop(): Promise<void> {
    if (this.torn) return;
    const hadNoSpeech = this.lastCommittedText === null;
    const silent = this.silentStop;
    await this.teardown();
    if (!silent && hadNoSpeech) {
      this.fail({ code: 'silence', message: SILENCE_MESSAGE });
      return;
    }
    this.callbacks.onStateChange('idle');
    this.resolveStop?.();
    this.resolveStop = null;
  }

  private fail(error: SpeechError): void {
    void this.teardown();
    this.callbacks.onError(error);
    this.callbacks.onStateChange('error');
    this.resolveStop?.();
    this.resolveStop = null;
  }

  private async teardown(): Promise<void> {
    if (this.torn) return;
    this.torn = true;
    if (this.finalizeTimer) {
      clearTimeout(this.finalizeTimer);
      this.finalizeTimer = null;
    }
    if (this.recognition) {
      // Detach before any stop/abort call so a late event never fires into torn-down state.
      this.recognition.onstart = null;
      this.recognition.onresult = null;
      this.recognition.onerror = null;
      this.recognition.onend = null;
      try {
        this.recognition.abort();
      } catch {
        // already stopped — nothing to do
      }
      this.recognition = null;
    }
    clearActiveSpeechSession(this.registryEntry);
  }
}

function mapRecognitionError(code: string): SpeechError {
  switch (code) {
    case 'not-allowed':
    case 'service-not-allowed':
      return { code: 'permission-denied', message: 'Microphone access was blocked.' };
    case 'audio-capture':
      return { code: 'no-microphone', message: 'No microphone was found on this device.' };
    case 'no-speech':
      return { code: 'silence', message: SILENCE_MESSAGE };
    case 'network':
      return { code: 'network-or-service', message: NETWORK_MESSAGE };
    default:
      return { code: 'network-or-service', message: NETWORK_MESSAGE };
  }
}

export const webSpeechProvider: SpeechProvider = {
  id: 'webspeech',
  isSupported(): boolean {
    return typeof window !== 'undefined' && window.isSecureContext && !!getRecognitionCtor();
  },
  createSession(callbacks: SpeechProviderCallbacks): SpeechSession {
    return new WebSpeechSession(callbacks);
  },
};
